/**
 * digitane 動画 Widget（embed.js）の埋め込み API の型。
 *
 * 正は digitane リポジトリの apps/video-player/src/embed.ts と
 * packages/video-shared/src/postMessage.ts。変更時はそちらに合わせること。
 */

/** 表示モード。fixed は動画だけを 16:9 で描き、高さは埋め込み側のコンテナで決める */
export type DigitaneVideoMode = 'floating' | 'fixed' | 'fullsize'

/** Widget が親ウィンドウへ送るイベントの種類 */
export type DigitaneVideoEventType =
  | 'video:ready'
  | 'video:started'
  | 'video:progress'
  | 'video:ended'
  | 'video:position'
  | 'video:resumed'
  | 'chapter:changed'
  | 'chapter:cleared'
  | 'navigate:town'
  | 'window:minimized'
  | 'window:resized'
  | 'action_point:triggered'
  | 'action_point:dismissed'
  | 'quiz:answered'

/** DigitaneVideoEventType を全て列挙した配列（フックで on を組み立てるのに使う） */
export const DIGITANE_VIDEO_EVENT_TYPES: readonly DigitaneVideoEventType[] = [
  'video:ready',
  'video:started',
  'video:progress',
  'video:ended',
  'video:position',
  'video:resumed',
  'chapter:changed',
  'chapter:cleared',
  'navigate:town',
  'window:minimized',
  'window:resized',
  'action_point:triggered',
  'action_point:dismissed',
  'quiz:answered',
] as const

/** イベントに付く payload。timestamp 以外はイベントの種類によって有無が変わる */
export interface DigitaneVideoEventPayload {
  readonly missionId?: number | null
  readonly chapterId?: number
  /** localSrc 経由の再生は videoId を持たないため null になりうる */
  readonly videoId?: number | null
  readonly videoCode?: string
  readonly chapterSeq?: number
  readonly chapterIndex?: number
  readonly chapterCount?: number
  readonly percent?: number
  readonly minimized?: boolean
  readonly height?: number
  readonly position?: number
  readonly duration?: number
  readonly sessionId?: string
  readonly siteId?: string
  readonly timestamp: number
  readonly actionPointId?: number
  readonly triggerTime?: number
  readonly actionType?: string
  readonly selectedIndex?: number
  readonly isDismissed?: boolean
}

export interface DigitaneVideoEvent {
  readonly source: 'digitane-video'
  readonly type: DigitaneVideoEventType
  readonly payload: DigitaneVideoEventPayload
}

export type DigitaneVideoEventHandler = (event: DigitaneVideoEvent) => void

export type DigitaneVideoEventHandlers = Partial<
  Record<DigitaneVideoEventType, DigitaneVideoEventHandler>
>

/** DigitaneVideo.init() に渡す設定 */
export interface DigitaneVideoConfig {
  /** iframe を追加するコンテナ要素または CSS セレクタ */
  readonly target: string | HTMLElement
  /** ミッション ID */
  readonly missionId?: number
  /** 動画コードで単体の動画を指定する。missionId と排他。チャプター一覧・視聴実績なし */
  readonly videoCode?: string
  /** Widget と同じオリジンにある MP4 ファイルを直接再生する（クローズ環境向け） */
  readonly localSrc?: string
  /** 表示モード */
  readonly mode?: DigitaneVideoMode
  /** サイト識別子（アナリティクス用） */
  readonly siteId?: string
  /** API 認証トークン */
  readonly apiToken?: string
  /** 埋め込み元が把握しているオンライン受講者かどうか（未指定なら iframe 側の API 判定に委ねる） */
  readonly isOnlineUser?: boolean
  /** 開始チャプターの seq（未指定なら先頭チャプターから開始する。0 も有効値） */
  readonly chapterSeq?: number
  /** 自動再生 */
  readonly autoplay?: boolean
  /** window:resized 通知に応じて iframe の高さを自動追従させる（Widget 側の既定は true） */
  readonly autoResize?: boolean
  /** イベントコールバック */
  readonly on?: DigitaneVideoEventHandlers
}

/** 埋め込み側が組み立てる設定（target と on を除いた、シリアライズ可能な値だけ） */
export type DigitaneVideoInitOptions = Omit<
  DigitaneVideoConfig,
  'target' | 'on'
>

export interface DigitaneVideoInstance {
  /** iframe を削除しイベントリスナーを解除する */
  destroy(): void
}

export interface DigitaneVideoApi {
  init(config: DigitaneVideoConfig): DigitaneVideoInstance
}

declare global {
  interface Window {
    /** embed.js の読み込み後に定義される */
    DigitaneVideo?: DigitaneVideoApi
  }
}
